import { I18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useCallback,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import trackOptimizelyEvent from 'shared/experiments/utils/trackOptimizelyEvent'
import { updateNowDataSuccessAction } from 'shared/store/ducks/now/actions'
import { type PageType } from 'types/cmsTypes'
import { type IState, type NowDataState } from 'types/redux'
import { type Tenant } from 'types/shopConfig'
import Button from 'views/components/atoms/Button'
import InputTextWithFloatingLabel from 'views/components/molecules/InputTextWithFloatingLabel'
import { useGlbZipCodeModalInfo } from 'views/components/organisms/NowGlbZipModalForm/hooks/useGlbZipCodeModalInfo'
import { validateZipCode } from 'views/components/organisms/NowShippingModal/NowShippingModalContent'
import { nowGlobalBarModalSubmitEvent } from 'views/utils/nowGlobalBar'
import './NowGlbZipModalForm.scss'

interface INowGlbZipModalForm {
  open: boolean
  i18n: I18n
  setModalOpen: Dispatch<SetStateAction<boolean>>
  zip: string
  pageType: PageType
  tenant: Tenant
}

const NowGlbZipModalForm: (props: INowGlbZipModalForm) => ReactElement = ({
  open,
  setModalOpen,
  zip,
  i18n,
  pageType,
  tenant,
}) => {
  const dispatch = useDispatch()
  const nowData: NowDataState = useSelector((state: IState) => state.nowData)
  const { currentZipValue, errorMessage, setErrorMessage, setCurrentZipValue } =
    useGlbZipCodeModalInfo({ zip, open })

  const clickStoreZipButton = useCallback(() => {
    if (!currentZipValue) {
      dispatch(updateNowDataSuccessAction({}))
      setErrorMessage('')
      return
    }

    if (validateZipCode(currentZipValue, i18n, setErrorMessage, tenant)) {
      dispatch(
        updateNowDataSuccessAction({
          ...nowData,
          zipCode: currentZipValue,
        })
      )
      trackOptimizelyEvent(nowGlobalBarModalSubmitEvent(pageType))
      setModalOpen(false)
    }
  }, [
    currentZipValue,
    dispatch,
    i18n,
    nowData,
    pageType,
    setErrorMessage,
    setModalOpen,
    tenant,
  ])

  const onZipChange = useCallback(
    e => setCurrentZipValue(e.target.value),
    [setCurrentZipValue]
  )

  const onEnterPress = useCallback(
    e => (e.key === 'Enter' ? clickStoreZipButton() : null),
    [clickStoreZipButton]
  )

  return (
    <>
      <div className="o-NowGlbZipModalForm-input-wrapper">
        <InputTextWithFloatingLabel
          placeholder={i18n._('general.label.nowGlobalBar.yourZip')}
          name="zip"
          defaultValue={zip}
          onKeyUp={onEnterPress}
          onChange={onZipChange}
        />
      </div>
      {errorMessage && (
        <ul className="o-nowUlInformation u-margin-small--bottom a-list-reset">
          <li className="o-nowErrorColor">
            <span className="o-errorMessage">{errorMessage}</span>
          </li>
        </ul>
      )}
      <Button name="storeZip" onClick={clickStoreZipButton}>
        <Trans id="general.label.nowGlobalBar.confirm" />
      </Button>
    </>
  )
}

export default NowGlbZipModalForm
